import { Formik, Form } from "formik";
import { colors } from "app/popleads/components/ui-kit/colors";
import React, { useMemo, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import ExtensionInputGroup, { StyledToggleLabel } from "./ExtensionInputGroup";
import { useExtensionContext } from "../ExtensionContext";
import {
    dealMarketingTypesOptions,
    dealChannelTypesOptions,
    DEAL_WON,
    DEAL_LOST,
    DEAL_CANCELLED,
    publicTypes,
    getCancellationReasons,
} from "../utils/constants";
import moment from "moment";
import ExtensionCancellationReasonFields from "./ExtensionCancellationReasonFields";
import useDealCancellationReasons from "app/popleads/hooks/useDealCancellationReasons";
import useCompanyData from "app/popleads/hooks/useCompanyData";
import { toast } from "react-toastify";
import useGetAllUnits from "app/popleads/hooks/useGetAllUnits";
import useGetCompanyConfigByKeys from "app/popleads/hooks/useGetCompanyConfigByKeys";
import { StyledItemContainer } from "./ExtensionViewDeal";
import Toggle from "app/popleads/components/ui-kit/Toggle";
import ButtonWithText from "app/popleads/components/ui-kit/Button";
import ExtensionCampaignSearchInput from "./ExtensionCampaignSearchInput";
import useActivityPreview from "app/popleads/hooks/useActivityPreview";
import { getPartnerUserIdFromName } from "../utils/deal-utils";
import { post } from "app/popleads/networking/RequestService";
import { isCipdCampaign } from "../utils/constants";
import ExtensionDealAttachments from "./ExtensionDealAttachments";
import { ExtensionSmall } from "app/popleads/components/ui-kit/Text";

export const StyledLabel = styled.label`
    display: block;
    font-size: 12px;
    color: ${colors.neutral[600]};
    margin-bottom: 0px;

    ${(props) =>
        props.required &&
        `
    &:after {
      content: '*';
      color: #ff4d4f;
      margin-left: 4px;
    }
  `}

    ${(props) =>
        props.disabled &&
        `
    color: #999;
    cursor: not-allowed;
  `}
`;
const options = [
    { label: "Yes", value: "Yes", activeVariant: "success" },
    { label: "No", value: "No", activeVariant: "neutral" },
];
const membershipOptions = [
    { label: "Student", value: "Student" },
    { label: "Center", value: "Center" },
];
const ExtensionEditDeal = () => {
    const lastLoadedDealIdRef = useRef(null);
    // Which campaign types this deal can be moved between is the backend's rule (DataTypes), so the
    // choices come from getDealById rather than a list kept here. Empty means the type is fixed.
    const [dealCampaignTypeOptions, setDealCampaignTypeOptions] = useState([]);
    const {
        setDealObject,
        dealObject,
        setIsDealEditing,
        selectedDeal,
        setSelectedDealWithId,
        isInhouseTypes,
        cvAttachment,
        setCvAttachment,
        refetchDeals,
        profile,
    } = useExtensionContext();
    const { companyUsers } = useCompanyData();
    const { departments } = useGetAllUnits();
    const { checkCertificateNameCondition } = useGetCompanyConfigByKeys();
    const certificateInputShow = checkCertificateNameCondition(selectedDeal.campaignType);
    const cancellationReasonsConfig = useDealCancellationReasons();

    const fetchQuery = async (inputValue, page) => {
        const requestObj = {
            fn: "getAccountSuggestions",
            accountName: inputValue ? inputValue : dealObject.accountName,
            page: page,
            pageSize: 100,
        };

        try {
            const response = await post(requestObj);
            const data = response.data;
            const options = data?.accounts?.map((item) => {
                return {
                    value: item.accountName,
                    label: item.accountName,
                };
            });

            return {
                options,
                hasMore: data.totalCount ? page * 100 < data.totalCount : false,
                additional: {
                    page: page + 1,
                },
            };
        } catch (error) {
            return {
                options: [],
                hasMore: false,
                additional: {
                    page: 1,
                },
            };
        }
    };

    const loadOptions = (inputValue, _, { page }) =>
        new Promise((resolve) => {
            const options = fetchQuery(inputValue, page);
            resolve(options);
        });
    const partnerOptions = useMemo(() => {
        return companyUsers.map((user) => ({
            label: user.name,
            value: user.id,
        }));
    }, [companyUsers]);
    useEffect(() => {
        return () => {
            setIsDealEditing(false);
        };
    }, []);
    // selectedDeal comes from the deal list already held in memory, which goes
    // stale as soon as the deal is edited anywhere outside this extension
    // session (e.g. the old CRM) - re-fetch on open so fields like CIPD's
    // dateOfBirth/physicalAddress/numberOfInstallments/nationalIdNumber reflect the
    // latest save.
    const editingDealId = selectedDeal?.id;
    useEffect(() => {
        if (editingDealId) refetchDeals();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // profile.olanak only updates a render or two after refetchDeals resolves,
    // so re-sync selectedDeal from it once it lands rather than chaining off
    // the refetch promise directly.
    useEffect(() => {
        if (editingDealId) setSelectedDealWithId(editingDealId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile?.olanak]);
    useEffect(() => {
        if (selectedDeal) {
            // dateOfBirth/physicalAddress/numberOfInstallments/cvAttachment/
            // nationalIdNumber are NOT set from selectedDeal here - listDealsByProfileId
            // (the sidebar summary list feeding selectedDeal) only reliably carries some
            // of them. The getDealById effect below is their single source of truth;
            // setting them here too would let a selectedDeal refresh race in and clobber
            // the correct values back to "" whenever the list projection omits a field.
            const updatedDeal = {
                ...selectedDeal,
                kapanmaTarihi: selectedDeal.kapanmaTarihi,
                channelType: selectedDeal.dealTypeChannel || "",
                unitName: selectedDeal.unitName || "",
                partnerName: selectedDeal.partnerName || "",
                partnerUserId: selectedDeal.partnerUserId || getPartnerUserIdFromName(selectedDeal, companyUsers),
                certificateName: selectedDeal.certificateName || "",
                certificateSurname: selectedDeal.certificateLastName || "",
            };
            // If the user switched to a different deal, the previous deal's CIPD
            // values are stale and must not leak onto this one - clear them and let
            // the getDealById effect below refill them for the new dealId.
            const isDifferentDeal = lastLoadedDealIdRef.current !== selectedDeal.id;
            lastLoadedDealIdRef.current = selectedDeal.id;
            setDealObject((prevDealObject) => ({
                ...updatedDeal,
                dateOfBirth: isDifferentDeal ? "" : prevDealObject.dateOfBirth || "",
                physicalAddress: isDifferentDeal ? "" : prevDealObject.physicalAddress || "",
                numberOfInstallments: isDifferentDeal ? "" : prevDealObject.numberOfInstallments || "",
                cvAttachment: isDifferentDeal ? null : prevDealObject.cvAttachment,
                nationalIdNumber: isDifferentDeal ? "" : prevDealObject.nationalIdNumber || "",
                membership: isDifferentDeal ? "" : prevDealObject.membership || "",
            }));
        }
    }, [companyUsers, selectedDeal]);
    // listDealsByProfileId (the sidebar list feeding selectedDeal) is a summary
    // projection and may not carry dateOfBirth/physicalAddress/numberOfInstallments/
    // cvAttachment/nationalIdNumber. getDealById is the full-detail read used elsewhere
    // (see AddActivityForm.js) - fetch it here too and layer the CIPD fields on top so
    // they show up even if the list endpoint omits them. This is the ONLY effect
    // allowed to set these five fields - see comment above.
    useEffect(() => {
        if (editingDealId) {
            setDealCampaignTypeOptions([]);
            post({ fn: "getDealById", dealId: editingDealId }).then((res) => {
                setDealCampaignTypeOptions(res?.data?.dealCampaignTypeOptions || []);
                const fullDeal = res?.data?.deal;
                if (!fullDeal) return;
                setDealObject((prevDealObject) => ({
                    ...prevDealObject,
                    dateOfBirth: fullDeal.dateOfBirth || prevDealObject.dateOfBirth || "",
                    physicalAddress: fullDeal.physicalAddress || prevDealObject.physicalAddress || "",
                    numberOfInstallments: fullDeal.numberOfInstallments || prevDealObject.numberOfInstallments || "",
                    cvAttachment: fullDeal.cvAttachment || prevDealObject.cvAttachment,
                    nationalIdNumber:
                        fullDeal.nationalIdNumber || fullDeal.nationalIDNumber || prevDealObject.nationalIdNumber || "",
                    membership: fullDeal.membership || prevDealObject.membership || "",
                }));
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editingDealId]);
    const membershipCampaign = dealObject.campaign || selectedDeal?.campaign;
    const membershipStatus = dealObject.status || selectedDeal?.durum || selectedDeal?.status;
    const showMembership = isCipdCampaign(membershipCampaign) && membershipStatus === DEAL_WON;
    useEffect(() => {
        if (membershipCampaign && membershipStatus && !showMembership && dealObject.membership) {
            setDealObject((prevDealObject) => ({ ...prevDealObject, membership: "" }));
        }
    }, [showMembership, membershipCampaign, membershipStatus, dealObject.membership, setDealObject]);
    const validateField = (fieldName, value, dealObject) => {
        switch (fieldName) {
            case "amount":
                // if (!value) {
                //     toast.warning("You need to enter the amount first");
                //     return true;
                // }
                // if (isNaN(value) || value <= 0) {
                //     toast.warning("Amount must be a positive number");
                //     return false;
                // }
                break;
            case "closingDate":
                if (!value && [DEAL_WON, DEAL_LOST, DEAL_CANCELLED].includes(dealObject.status)) {
                    toast.warning("Please choose a closing date");
                    return false;
                }
                break;
            case "type":
                if (!value && dealObject.status === DEAL_WON) {
                    toast.warning("You need to enter the type first");
                    return false;
                }
                break;
            case "channelType":
                if (
                    !value &&
                    ["Marketing", "Marketing Cross Sale"].includes(dealObject.type) &&
                    dealObject.status === DEAL_WON
                ) {
                    toast.warning("Please select marketing channel");
                    return false;
                }
                break;
            // case "certificateName":
            // case "certificateSurname":
            //     if (!value && publicTypes.includes(dealObject.dealCampaignType) && dealObject.status === DEAL_WON) {
            //         toast.warning(
            //             `Please provide a certificate ${fieldName === "certificateName" ? "name" : "surname"}`
            //         );
            //         return false;
            //     }
            //break;
            default:
                return true;
        }
        return true;
    };

    const handleInputChange = (field, value) => {
        if (validateField(field, value)) {
            const newDealObject = { ...dealObject, [field]: value };
            setDealObject(newDealObject);
        }
    };

    // getDealById's `cvAttachment` only carries id/attachmentId/fileId + filename -
    // no download link (see getAttachmentId in ExtensionContext.js, which never
    // looks for one either). The actual downloadLink for that same file lives on
    // profile.attachments (populated from getAttachments), so look it up there by
    // id instead of expecting the deal-embedded copy to carry it.
    const cvAttachmentId =
        dealObject.cvAttachment?.id ||
        dealObject.cvAttachment?.attachmentId ||
        dealObject.cvAttachment?.fileId ||
        dealObject.cvAttachmentId;
    const matchedCvAttachment = profile?.attachments?.find(
        (attachment) => attachment.id === cvAttachmentId || attachment.attachmentId === cvAttachmentId
    );
    const cvAttachmentForDisplay = dealObject.cvAttachment
        ? {
              ...dealObject.cvAttachment,
              ...(matchedCvAttachment ? { downloadLink: matchedCvAttachment.downloadLink } : {}),
          }
        : null;

    const [vatIncludes, setVatIncludes] = useState(dealObject?.vatIncludes === 1 ? "Yes" : "No");
    const [selfInvestment, setSelfInvestment] = useState(dealObject?.isSelfInvestment === 1 ? "Yes" : "No");
    const isDealPublicType = publicTypes.includes(dealObject.dealCampaignType) && dealObject.durum === DEAL_WON;
    const isCipdDeal = isCipdCampaign(dealObject.campaign);
    const inConsultingType =
        dealObject.dealCampaignType === "Consulting" ||
        dealObject.dealCampaignType === "Coaching" ||
        dealObject.dealCampaignType === "E-Learning" ||
        dealObject.dealCampaignType === "EdTech";
    const dealActivityId = dealObject.activityId > 0;
    // Stored types are not always spelled like the options ("in-house" vs "In-house"); show the option's spelling.
    const selectedDealCampaignType =
        dealCampaignTypeOptions.find(
            (type) => type.toLowerCase() === dealObject.dealCampaignType?.trim().toLowerCase()
        ) || dealObject.dealCampaignType;
    // A cancelled deal's reason and note stay editable; the cancellation date is stamped by the backend
    // and only shown. Without a configured reason list there is nothing to pick, so the fields are hidden.
    const isCancelledDeal = (dealObject.status || dealObject.durum) === DEAL_CANCELLED;
    const showCancellationFields =
        isCancelledDeal && getCancellationReasons(cancellationReasonsConfig, dealObject.dealCampaignType).length > 0;

    const { getActivityPreview } = useActivityPreview();
    const [activityPreview, setActivityPreview] = useState(null);

    useEffect(() => {
        if (dealObject.activityId) {
            getActivityPreview(dealObject.activityId).then((data) => {
                setActivityPreview(data.activity);
            });
        }
    }, [dealObject.activityId]);

    let courseNameLabel = "";
    let newCourseNameLabel = "";
    if (dealObject.dealCampaignType === "E-Learning" || dealObject.dealCampaignType === "EdTech") {
        courseNameLabel = "E-Learning Bundles";
        newCourseNameLabel = "New E-Learning Bundles";
    } else if (dealObject.dealCampaignType === "Consulting") {
        courseNameLabel = "Type of Consulting";
        newCourseNameLabel = "New Consulting Type";
    } else if (dealObject.dealCampaignType === "Coaching") {
        courseNameLabel = "Type of Coaching";
        newCourseNameLabel = "New Coaching Type";
    } else {
        courseNameLabel = "Course Name";
        newCourseNameLabel = "New Course Name";
    }
    const courseNameOptions = [
        ...(activityPreview?.topicList || []).map((item) => ({
            label: item.topicName,
            value: item.topicName,
        })),
        { label: "Type a new " + courseNameLabel, value: "newCourseName" },
    ];

    return (
        <Formik initialValues={dealObject}>
            {() => (
                <Form style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    <ExtensionInputGroup
                        variant="l3rnTech"
                        label="Deal Name"
                        type="input"
                        value={dealObject.ad}
                        onChange={(e) => {
                            const value = e.target ? e.target.value : e;
                            handleInputChange("ad", value);
                        }}
                        placeholder="Please enter deal name"
                    />
                    <ExtensionInputGroup
                        variant="l3rnTech"
                        key={"single-select"}
                        placeholder={"Add Account"}
                        label="Account Name"
                        type="select"
                        isClearable={true}
                        isSearchable={true}
                        isCreatable={true}
                        isMulti={false}
                        loadOptions={loadOptions}
                        additional={{ page: 1 }}
                        value={{ label: dealObject.accountName, value: dealObject.accountName }}
                        onChange={(selected) => {
                            handleInputChange("accountName", selected ? selected.value : "");
                            if (selected) {
                                setDealObject({
                                    ...dealObject,
                                    accountName: selected.label,
                                });
                            } else {
                                setDealObject({ ...dealObject, accountName: "" });
                            }
                        }}
                    />
                    {dealCampaignTypeOptions.length > 1 && (
                        <ExtensionInputGroup
                            variant="l3rnTech"
                            label="Campaign Type"
                            type="select"
                            options={dealCampaignTypeOptions.map((type) => ({ label: type, value: type }))}
                            value={
                                selectedDealCampaignType
                                    ? { label: selectedDealCampaignType, value: selectedDealCampaignType }
                                    : ""
                            }
                            onChange={(selected) => {
                                // The reason list depends on the campaign type, so a reason the new type
                                // does not offer is dropped and has to be picked again.
                                const keepsReason = getCancellationReasons(
                                    cancellationReasonsConfig,
                                    selected.value
                                ).includes(dealObject.cancellationReason);
                                setDealObject({
                                    ...dealObject,
                                    dealCampaignType: selected.value,
                                    ...(isCancelledDeal && !keepsReason
                                        ? { cancellationReason: "", cancellationNote: "" }
                                        : {}),
                                });
                            }}
                            placeholder="Select Campaign Type"
                        />
                    )}
                    {!dealActivityId && (
                        <ExtensionCampaignSearchInput
                            optionClick={(value) => {
                                handleInputChange("etiketId", value?.id || "");
                                setDealObject({
                                    ...dealObject,
                                    etiketId: value?.id || "",
                                    campaign: value,
                                });
                            }}
                            value={dealObject.campaign}
                            label="Campaign Name"
                        />
                    )}

                    {dealObject.durum === DEAL_WON &&
                        (isInhouseTypes(dealObject.dealCampaignType) || inConsultingType) && (
                            <>
                                <ExtensionInputGroup
                                    variant="l3rnTech"
                                    label={courseNameLabel}
                                    type="select"
                                    inpType="text"
                                    onChange={(selected) => {
                                        handleInputChange("courseName", selected.value);
                                    }}
                                    options={courseNameOptions}
                                    value={
                                        dealObject.courseName
                                            ? dealObject.courseName === "newCourseName"
                                                ? { label: "Type a new " + courseNameLabel, value: "newCourseName" }
                                                : { label: dealObject.courseName, value: dealObject.courseName }
                                            : ""
                                    }
                                    placeholder={`Select ${courseNameLabel}`}
                                />

                                {dealObject?.courseName === "newCourseName" && (
                                    <ExtensionInputGroup
                                        variant="l3rnTech"
                                        label={`New ${courseNameLabel}`}
                                        type="input"
                                        inpType="text"
                                        onChange={(e) => {
                                            const value = e.target ? e.target.value : e;
                                            handleInputChange("newCourseName", value);
                                        }}
                                        value={dealObject.newCourseName}
                                        placeholder={`Enter ${newCourseNameLabel}`}
                                    />
                                )}
                            </>
                        )}

                    <ExtensionInputGroup
                        variant="l3rnTech"
                        label={`${isInhouseTypes(dealObject.dealCampaignType) ? "Amount USD" : "Unit Amount USD"}`}
                        type="input"
                        inpType="number"
                        leftIcon="dollar-sign"
                        value={dealObject.amount}
                        onChange={(e) => {
                            const value = e.target ? e.target.value : e;
                            handleInputChange("amount", value);
                        }}
                        minLength={1}
                        placeholder={`Please enter ${
                            isInhouseTypes(dealObject.dealCampaignType) ? "amount" : "unit amount"
                        }`}
                    />

                    {!isInhouseTypes(dealObject.dealCampaignType) && (
                        <div>
                            <StyledLabel>Number of Units</StyledLabel>
                            <div style={{ display: "flex", gap: "16px", marginTop: "4px" }}>
                                <ButtonWithText
                                    label="Decrease"
                                    icon="remove"
                                    onClick={() => {
                                        const currentValue = Number(dealObject.units) || 1;

                                        if (currentValue > 1) {
                                            handleInputChange("units", currentValue - 1);
                                        }
                                    }}
                                    iconType="material"
                                    variant="passive"
                                    buttonStyle="default"
                                    size="circle"
                                />
                                {dealObject.units}
                                <ButtonWithText
                                    label="Increase"
                                    icon="add"
                                    onClick={() => {
                                        const currentValue = Number(dealObject.units) || 0;
                                        handleInputChange("units", currentValue + 1);
                                    }}
                                    iconType="material"
                                    variant="l3rnTech"
                                    buttonStyle="default"
                                    size="circle"
                                />
                            </div>
                        </div>
                    )}

                    <ExtensionInputGroup
                        variant="l3rnTech"
                        label="Paid Amount"
                        type="input"
                        disabled={true}
                        inpType="number"
                        leftIcon="dollar-sign"
                        value={dealObject.paidAmount}
                        onChange={(e) => {
                            const value = e.target ? e.target.value : e;
                            handleInputChange("paidAmount", value);
                        }}
                        placeholder="Please enter paid amount"
                    />

                    {isCancelledDeal && (
                        <ExtensionInputGroup
                            variant="l3rnTech"
                            label="Cancellation Date"
                            type="input"
                            disabled={true}
                            value={
                                dealObject.cancellationDate
                                    ? moment(dealObject.cancellationDate).format("MMM DD, YYYY")
                                    : ""
                            }
                            onChange={() => {}}
                            placeholder="N/A"
                        />
                    )}

                    {showCancellationFields && (
                        <ExtensionCancellationReasonFields
                            dealCampaignType={dealObject.dealCampaignType}
                            cancellationReason={dealObject.cancellationReason}
                            cancellationNote={dealObject.cancellationNote}
                            onChange={(cancellation) =>
                                setDealObject((prevDealObject) => ({ ...prevDealObject, ...cancellation }))
                            }
                        />
                    )}

                    {(dealObject.status === DEAL_WON || dealObject.durum === DEAL_WON) && (
                        <ExtensionInputGroup
                            variant="l3rnTech"
                            label="Closing Date"
                            type="date"
                            value={dealObject.closingDate || dealObject.kapanmaTarihi}
                            onChange={(date) => {
                                handleInputChange("closingDate", date);
                                handleInputChange("kapanmaTarihi", date);
                            }}
                            placeholder="Please select closing date"
                            noTimeSelection={true}
                        />
                    )}

                    {(dealObject.status === DEAL_WON || dealObject.durum === DEAL_WON) && (
                        <ExtensionInputGroup
                            variant="l3rnTech"
                            label="Type"
                            type="select"
                            options={dealMarketingTypesOptions}
                            value={dealObject.type ? { label: dealObject.type, value: dealObject.type } : ""}
                            onChange={(selected) => {
                                handleInputChange("type", selected.value);
                                if (selected.value !== "Marketing" && selected.value !== "Marketing Cross Sale") {
                                    setDealObject((prevDealObject) => ({
                                        ...prevDealObject,
                                        dealTypeChannel: "",
                                        typeChannel: "",
                                        channelType: "",
                                    }));
                                }
                                if (dealObject.dealCampaignType !== "L&D Broker") {
                                    setDealObject((prevDealObject) => ({
                                        ...prevDealObject,
                                        vendorPaymentAmount: "",
                                        leoronMarginAmount: "",
                                    }));
                                }
                                if (selected.value !== "Re-Seller Partnership") {
                                    setDealObject((prevDealObject) => ({
                                        ...prevDealObject,
                                        endUser: "",
                                    }));
                                }
                            }}
                            placeholder="Please select type"
                        />
                    )}

                    {(dealObject.status === DEAL_WON || dealObject.durum === DEAL_WON) &&
                        (dealObject.type === "Marketing" || dealObject.type === "Marketing Cross Sale") && (
                            <ExtensionInputGroup
                                variant="l3rnTech"
                                label="Marketing Channel"
                                type="select"
                                options={dealChannelTypesOptions}
                                value={
                                    dealObject.dealTypeChannel
                                        ? { label: dealObject.dealTypeChannel, value: dealObject.dealTypeChannel }
                                        : ""
                                }
                                onChange={(selected) => {
                                    setDealObject({
                                        ...dealObject,
                                        dealTypeChannel: selected.value,
                                        typeChannel: selected.value,
                                        channelType: selected.value,
                                    });
                                }}
                                placeholder="Please select marketing channel"
                            />
                        )}
                    {(dealObject.status === DEAL_WON || dealObject.durum === DEAL_WON) &&
                        dealObject.type === "Re-Seller Partnership" && (
                            <ExtensionInputGroup
                                label="End User"
                                type="input"
                                inpType="text"
                                onChange={(e) => {
                                    const value = e.target ? e.target.value : e;
                                    handleInputChange("endUser", value);
                                }}
                                value={dealObject.endUser}
                                placeholder="Please enter end user"
                                minLength={1}
                                variant="l3rnTech"
                            />
                        )}
                    {dealObject.dealCampaignType === "L&D Broker" && (
                        <>
                            <ExtensionInputGroup
                                label="Amount for Vendor Payment"
                                type="input"
                                inpType="number"
                                leftIcon="dollar-sign"
                                onKeyDown={(e) => {
                                    if (e.key === "-" || e.key === "e" || e.key === "+") {
                                        e.preventDefault();
                                    }
                                }}
                                onChange={(e) => {
                                    const value = e.target ? e.target.value : e;
                                    handleInputChange("vendorPaymentAmount", value);
                                }}
                                value={dealObject.vendorPaymentAmount}
                                minLength={1}
                                placeholder="Enter amount for vendor payment"
                                variant="l3rnTech"
                            />
                            <ExtensionInputGroup
                                label="Margin Amount for LEORON"
                                type="input"
                                inpType="number"
                                leftIcon="dollar-sign"
                                onKeyDown={(e) => {
                                    if (e.key === "-" || e.key === "e" || e.key === "+") {
                                        e.preventDefault();
                                    }
                                }}
                                onChange={(e) => {
                                    const value = e.target ? e.target.value : e;
                                    handleInputChange("leoronMarginAmount", value);
                                }}
                                value={dealObject.leoronMarginAmount}
                                minLength={1}
                                placeholder="Enter margin amount for LEORON"
                                variant="l3rnTech"
                            />
                        </>
                    )}

                    <ExtensionInputGroup
                        variant="l3rnTech"
                        label="Department"
                        type="select"
                        options={departments}
                        onChange={(selected) => {
                            if (selected) {
                                const newDealObject = { ...dealObject };
                                newDealObject.unitId = selected.value;
                                newDealObject.unitName = selected.label;
                                setDealObject(newDealObject);
                            }
                        }}
                        value={
                            dealObject?.unitName
                                ? {
                                      label: dealObject?.unitName,
                                      value: dealObject?.unitId,
                                  }
                                : ""
                        }
                        placeholder="Please select department"
                    />

                    <ExtensionInputGroup
                        variant="l3rnTech"
                        label="Partner"
                        type="select"
                        options={partnerOptions}
                        onChange={(selected) => {
                            if (selected) {
                                setDealObject({
                                    ...dealObject,
                                    partnerName: selected.label,
                                    partnerUserId: selected.value,
                                });
                            } else {
                                setDealObject({ ...dealObject, partnerName: "", partnerUserId: "" });
                            }
                        }}
                        value={
                            dealObject?.partnerName
                                ? {
                                      label: dealObject?.partnerName,
                                      value: dealObject?.partnerUserId,
                                  }
                                : ""
                        }
                        placeholder="Please select partner"
                        isClearable={true}
                    />
                    <StyledItemContainer>
                        <StyledToggleLabel>Vat Included</StyledToggleLabel>
                        <Toggle
                            variant="l3rnTech"
                            size="small"
                            setSelected={(value) => {
                                handleInputChange("vatIncludes", value === "Yes" ? 1 : 0);
                                setVatIncludes(value);
                            }}
                            selected={vatIncludes}
                            options={options}
                        />
                    </StyledItemContainer>
                    {isDealPublicType && (
                        <StyledItemContainer>
                            <StyledToggleLabel>Self Investment</StyledToggleLabel>
                            <Toggle
                                variant="l3rnTech"
                                size="small"
                                setSelected={(value) => {
                                    setSelfInvestment(value);
                                    // Don't clear numberOfInstallments/nationalIdNumber here when
                                    // switching to "No" - createActivityURL (ExtensionContext.js)
                                    // already omits numberOfInstallments from the request unless
                                    // isSelfInvestment is "Yes", and always sends nationalIdNumber
                                    // regardless, so clearing buys nothing at submit time. It used
                                    // to wipe both fields on every toggle, which meant flipping
                                    // No -> Yes again showed them empty instead of restoring what
                                    // the user had typed.
                                    handleInputChange("isSelfInvestment", value === "Yes" ? 1 : 0);
                                }}
                                selected={selfInvestment}
                                options={options}
                            />
                        </StyledItemContainer>
                    )}
                    {showMembership && (
                        <ExtensionInputGroup
                            variant="l3rnTech"
                            label="Membership"
                            type="select"
                            options={membershipOptions}
                            value={
                                dealObject.membership
                                    ? { label: dealObject.membership, value: dealObject.membership }
                                    : ""
                            }
                            onChange={(selected) =>
                                setDealObject((prevDealObject) => ({
                                    ...prevDealObject,
                                    membership: selected?.value || "",
                                }))
                            }
                            placeholder="Select Membership"
                            required={true}
                        />
                    )}
                    {isDealPublicType && selfInvestment === "Yes" && (
                        <ExtensionInputGroup
                            variant="l3rnTech"
                            label="National ID Number"
                            type="input"
                            inpType="text"
                            onChange={(e) => {
                                const value = e.target ? e.target.value : e;
                                handleInputChange("nationalIdNumber", value);
                            }}
                            value={dealObject.nationalIdNumber || ""}
                            placeholder="Please enter national ID number"
                            minLength={1}
                            maxLength={99}
                            required={true}
                        />
                    )}
                    {isDealPublicType && isCipdDeal && selfInvestment === "Yes" && (
                        <ExtensionInputGroup
                            variant="l3rnTech"
                            label="Number of Installments"
                            type="input"
                            // type="number" inputs ignore the maxlength attribute in every
                            // browser, so the cap below relied entirely on the onChange
                            // slice - "text" makes maxLength an actual DOM-enforced limit
                            // too (same pattern as National ID Number). inputMode/pattern
                            // keep the numeric-only intent (numeric mobile keypad, digit
                            // validation hint) without reintroducing type="number"'s bug.
                            inpType="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            min={1}
                            maxLength={7}
                            debounceTime={0}
                            // The underlying input is a DebounceInput that (while focused)
                            // does not let an incoming `value` prop override what the user
                            // just typed, so filtering only in onChange left invalid
                            // characters visible on screen even though state was already
                            // corrected underneath. Block non-digit keystrokes and pasted
                            // text before they ever reach the input's value at all.
                            onKeyDown={(e) => {
                                if (e.ctrlKey || e.metaKey || e.key.length > 1) return; // Backspace, Tab, arrows, Ctrl+V, ...
                                if (!/^[0-9]$/.test(e.key)) e.preventDefault();
                            }}
                            onPaste={(e) => {
                                e.preventDefault();
                                const pasted = (e.clipboardData || window.clipboardData)
                                    .getData("text")
                                    .replace(/[^0-9]/g, "")
                                    .slice(0, 7);
                                handleInputChange("numberOfInstallments", pasted);
                            }}
                            onChange={(e) => {
                                const value = e.target ? e.target.value : e;
                                const positiveInteger = value.replace(/[^0-9]/g, "").slice(0, 7);
                                handleInputChange("numberOfInstallments", positiveInteger);
                            }}
                            value={dealObject.numberOfInstallments || ""}
                            placeholder="Please enter number of installments"
                            required={true}
                        />
                    )}
                    {isDealPublicType && isCipdDeal && (
                        <>
                            <ExtensionInputGroup
                                variant="l3rnTech"
                                label="Date of Birth"
                                type="date"
                                onChange={(date) => {
                                    handleInputChange("dateOfBirth", date);
                                }}
                                value={dealObject.dateOfBirth}
                                placeholder="Please select date of birth"
                                noTimeSelection={true}
                                required={true}
                            />
                            <ExtensionInputGroup
                                variant="l3rnTech"
                                label="Physical Address"
                                type="textarea"
                                onChange={(e) => {
                                    const value = e.target ? e.target.value : e;
                                    handleInputChange("physicalAddress", value);
                                }}
                                value={dealObject.physicalAddress || ""}
                                placeholder="Please enter physical address"
                                required={true}
                            />
                            <div>
                                <StyledLabel required={true}>CV Attachment</StyledLabel>
                                <ExtensionDealAttachments
                                    maxFiles={1}
                                    dealId={editingDealId}
                                    onFilesChange={setCvAttachment}
                                    existingFiles={cvAttachmentForDisplay ? [cvAttachmentForDisplay] : []}
                                    isCvAttachment={true}
                                    onCvDeleted={() =>
                                        setDealObject((prevDealObject) => ({
                                            ...prevDealObject,
                                            cvAttachment: null,
                                            cvAttachmentId: null,
                                        }))
                                    }
                                />
                                {!cvAttachment?.length && !dealObject.cvAttachment && (
                                    <ExtensionSmall style={{ color: "#ff4d4f" }}>
                                        CV attachment is required for CIPD deals
                                    </ExtensionSmall>
                                )}
                            </div>
                        </>
                    )}
                    {(dealObject.status === DEAL_WON || dealObject.durum === DEAL_WON) &&
                        certificateInputShow &&
                        !dealActivityId && (
                            <>
                                <ExtensionInputGroup
                                    variant="l3rnTech"
                                    label="Certificate Name"
                                    type="input"
                                    value={dealObject.certificateName}
                                    onChange={(e) => {
                                        const value = e.target ? e.target.value : e;
                                        handleInputChange("certificateName", value);
                                    }}
                                    placeholder="Please enter certificate name"
                                />

                                <ExtensionInputGroup
                                    variant="l3rnTech"
                                    label="Certificate Lastname"
                                    type="input"
                                    value={dealObject.certificateLastName}
                                    onChange={(e) => {
                                        const value = e.target ? e.target.value : e;
                                        handleInputChange("certificateLastName", value);
                                    }}
                                    placeholder="Please enter certificate lastname"
                                />
                            </>
                        )}
                </Form>
            )}
        </Formik>
    );
};

export default ExtensionEditDeal;
